import React from 'react';
import classNames from 'classnames';
import usFlagSmall from '@uswds/uswds/dist/img/us_flag_small.png';
import iconDotGov from '@uswds/uswds/dist/img/icon-dot-gov.svg';
import iconHttps from '@uswds/uswds/dist/img/icon-https.svg';
import BannerExtension from './BannerExtension';
import Accordion, { useAccordionContext } from '../Accordion';

export interface BannerProps {
  extensionType?: BannerExtension;
}

const bannerId = 'gov-banner';

const BannerHeader = (): JSX.Element => {
  const { isOpen } = useAccordionContext();
  return (
    <header className={classNames('usa-banner__header', { 'usa-banner__header--expanded': isOpen(bannerId) })}>
      <div className="usa-banner__inner">
        <div className="grid-col-auto">
          <img aria-hidden="true" className="usa-banner__header-flag" src={usFlagSmall} alt="U.S. flag" />
        </div>
        <div className="grid-col-fill tablet:grid-col-auto">
          <p className="usa-banner__header-text">An official website of the United States government</p>
          <p className="usa-banner__header-action" aria-hidden="true">
            Here’s how you know
          </p>
        </div>
        <Accordion.Button controls={bannerId} className="usa-banner__button">
          <span className="usa-banner__button-text">Here’s how you know</span>
        </Accordion.Button>
      </div>
    </header>
  );
};

const Banner = ({ extensionType = BannerExtension.GOV }: BannerProps): JSX.Element => {
  const TLD = extensionType === BannerExtension.GOV ? '.gov' : '.mil';

  return (
    <section className="usa-banner" aria-label="Official website of the United States government">
      <Accordion>
        <BannerHeader />
        <Accordion.Content id={bannerId} className="usa-banner__content">
          <div className="grid-row grid-gap-lg">
            <div className="usa-banner__guidance tablet:grid-col-6">
              <img
                className="usa-banner__icon usa-media-block__img"
                src={iconDotGov}
                role="img"
                alt=""
                aria-hidden="true"
              />
              <div className="usa-media-block__body">
                <p>
                  <strong>Offical websites use {TLD}</strong>
                  <br />A <strong>{TLD}</strong> website belongs to an official government organization in the United
                  States.
                </p>
              </div>
            </div>
            <div className="usa-banner__guidance tablet:grid-col-6">
              <img
                className="usa-banner__icon usa-media-block__img"
                src={iconHttps}
                role="img"
                alt=""
                aria-hidden="true"
              />
              <div className="usa-media-block__body">
                <p>
                  <strong>Secure {TLD} websites use HTTPS</strong>
                  <br />A <strong>lock</strong> (
                  <span className="icon-lock">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="52"
                      height="64"
                      viewBox="0 0 52 64"
                      className="usa-banner__lock-image"
                      role="img"
                      aria-labelledby="banner-lock-description-default"
                      focusable="false"
                    >
                      <title id="banner-lock-title-default">Lock</title>
                      <desc id="banner-lock-description-default">A locked padlock</desc>
                      <path
                        fill="#000000"
                        fillRule="evenodd"
                        d="M26 0c10.493 0 19 8.507 19 19v9h3a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V32a4 4 0 0 1 4-4h3v-9C7 8.507 15.507 0 26 0zm0 8c-5.979 0-10.843 4.77-10.996 10.712L15 19v9h22v-9c0-6.075-4.925-11-11-11z"
                      />
                    </svg>
                  </span>
                  ) or <strong>https://</strong> means you’ve safely connected to the {TLD} website. Share sensitive
                  information only on official, secure websites.
                </p>
              </div>
            </div>
          </div>
        </Accordion.Content>
      </Accordion>
    </section>
  );
};

export default Banner;
